import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as affiliateService from '../../services/adminAffiliate.service';
import { uploadImage } from '../../services/upload.service';
import MemberStatusBadge from '../../components/common/MemberStatusBadge';
import ImageDropZone from '../../components/common/ImageDropZone';
import RichTextEditor from '../../components/common/RichTextEditor';

// Same real header choices as the bulk email composer (AdminBulkEmail.jsx) —
// Jenn, 2026-09-11: "we need to be able to choose our banners" — reused
// here rather than rebuilt, same backend rendering (email.js's
// BULK_EMAIL_HEADERS) so what's picked here is exactly what goes out.
const HEADER_STYLES = [
  { key: 'jenn_photo', label: "Hey, it's Jenn", description: 'Your photo banner' },
  { key: 'compass',    label: 'Compass',         description: 'Dark banner, gold compass icon' },
];

/**
 * Same shape as the partner's own self-service dashboard (AffiliateDashboard.jsx) —
 * deliberately mirrored so Team365 sees exactly what the partner sees, plus the
 * admin-only payout action. This is the drill-down the flat /admin/affiliates
 * list never had — Jenn flagged not being able to open a partner or know what
 * their own view looked like as a real trust problem, since this ledger is money.
 */
export default function AdminAffiliateDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showPayout, setShowPayout] = useState(false);
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sendingIntake, setSendingIntake] = useState(false);
  const [intakeSent, setIntakeSent] = useState(false);
  const [intakePreview, setIntakePreview] = useState(null); // {subject, body, intakeUrl, toEmail, html} while the review modal is open
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [renderingHtml, setRenderingHtml] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvalEmailPreview, setApprovalEmailPreview] = useState(null); // {subject, body, dashboardUrl, toEmail, html}
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approvalSent, setApprovalSent] = useState(false);
  const [renderingApprovalHtml, setRenderingApprovalHtml] = useState(false);

  const handleOpenIntakePreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      setIntakePreview(await affiliateService.getIntakeEmailPreview(id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Jenn, 2026-09-10: "that shows me raw text in fields, not what the email
  // preview looks like." Re-renders the real styled HTML as she edits,
  // debounced so it's not a request per keystroke. Also re-renders on a
  // header-style switch — Jenn, 2026-09-11: "we need...a live preview of
  // what the email will look like...the banner image needs to be seen."
  useEffect(() => {
    if (!intakePreview) return;
    const timeout = setTimeout(async () => {
      setRenderingHtml(true);
      try {
        const html = await affiliateService.renderIntakeEmailPreview(id, { subject: intakePreview.subject, body: intakePreview.body, headerStyle: intakePreview.headerStyle });
        setIntakePreview(prev => (prev ? { ...prev, html } : prev));
      } catch { /* keep showing the last good render */ }
      finally { setRenderingHtml(false); }
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakePreview?.subject, intakePreview?.body, intakePreview?.headerStyle]);

  const handleSendIntakeEmail = async () => {
    setSendingIntake(true);
    setIntakeSent(false);
    try {
      await affiliateService.sendIntakeEmail(id, { subject: intakePreview.subject, body: intakePreview.body, headerStyle: intakePreview.headerStyle });
      setIntakeSent(true);
      setIntakePreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this email.');
    } finally {
      setSendingIntake(false);
    }
  };

  const toggleActive = async () => {
    const next = !data.partner.is_active;
    if (!window.confirm(next
      ? `Reactivate ${data.partner.name} as a partner?`
      : `Archive ${data.partner.name}? They'll stop earning new commissions, but their history stays intact.`
    )) return;
    setArchiving(true);
    try {
      await affiliateService.updatePartner(id, { is_active: next });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this partner.');
    } finally {
      setArchiving(false);
    }
  };

  // Jenn, 2026-09-11: "nothing goes live without a member of Team365
  // signing off" — this is the one and only way an affiliate's link,
  // $1 offer, and real landing page turn on.
  // "once we [approve], we send an email to the affiliate asking them to
  // visit their dashboard" — opening the review modal right after a
  // successful approve keeps that as one flow, not a separate thing to
  // remember to come back and do.
  const handleApprove = async () => {
    if (!window.confirm(`Go live for ${data.partner.name}? Their link, $1 offer, and real landing page will all become active immediately.`)) return;
    setApproving(true);
    setError(null);
    try {
      await affiliateService.approveLandingPage(id);
      load();
      handleOpenApprovalPreview();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not approve this affiliate.');
    } finally {
      setApproving(false);
    }
  };

  const handleOpenApprovalPreview = async () => {
    setError(null);
    try {
      setApprovalEmailPreview(await affiliateService.getApprovalEmailPreview(id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this preview.');
    }
  };

  useEffect(() => {
    if (!approvalEmailPreview) return;
    const timeout = setTimeout(async () => {
      setRenderingApprovalHtml(true);
      try {
        const html = await affiliateService.renderApprovalEmailPreview(id, { subject: approvalEmailPreview.subject, body: approvalEmailPreview.body, headerStyle: approvalEmailPreview.headerStyle });
        setApprovalEmailPreview(prev => (prev ? { ...prev, html } : prev));
      } catch { /* keep showing the last good render */ }
      finally { setRenderingApprovalHtml(false); }
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalEmailPreview?.subject, approvalEmailPreview?.body, approvalEmailPreview?.headerStyle]);

  const handleSendApprovalEmail = async () => {
    setSendingApproval(true);
    setApprovalSent(false);
    try {
      await affiliateService.sendApprovalEmail(id, { subject: approvalEmailPreview.subject, body: approvalEmailPreview.body, headerStyle: approvalEmailPreview.headerStyle });
      setApprovalSent(true);
      setApprovalEmailPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this email.');
    } finally {
      setSendingApproval(false);
    }
  };

  const handleUnapprove = async () => {
    if (!window.confirm(`Take ${data.partner.name}'s link, $1 offer, and landing page offline? They'll go back to "waiting on Team365" until approved again.`)) return;
    setApproving(true);
    setError(null);
    try {
      await affiliateService.unapproveLandingPage(id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not take this affiliate offline.');
    } finally {
      setApproving(false);
    }
  };

  const onUpdatePhoto = async (photoUrl) => {
    try {
      await affiliateService.updateIntakeProfile(id, { photo_url: photoUrl });
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not save this affiliate's photo.");
    }
  };

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await affiliateService.getPartnerDetail(id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this partner.');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <AdminNav />
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm mt-6">{error}</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto"><AdminNav /><p className="text-sm text-gray-400 mt-6">Loading…</p></div>
      </DashboardLayout>
    );
  }

  const { partner, owed, paid, commissions, payouts, referral_count, referred_members, link_active, awaiting_approval, trial_code, landing_page_preview_url, confirmation_page_preview_url } = data;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <AdminNav />

        <Link to="/admin/affiliates" className="text-xs text-gray-400 hover:text-reset-terracotta">&larr; All partners</Link>

        <div className="flex items-center justify-between mt-2 mb-1 flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-serif text-2xl font-bold text-reset-brown">{partner.name}</h1>
            {partner.is_active === false && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Archived</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(v => !v)}
              className="px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
            >
              {editing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={toggleActive}
              disabled={archiving}
              className="px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              {partner.is_active === false ? 'Reactivate' : 'Archive'}
            </button>
            <button
              onClick={() => setShowPayout(true)}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
            >
              Record payout
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-1">{partner.email}</p>
        <p className="text-xs bg-reset-cream inline-block px-3 py-1 rounded-full text-gray-500 mb-3">
          The referrals, owed/paid totals, and commissions below match what {partner.name.split(' ')[0]} sees on their own dashboard — everything else on this page (payout/edit/archive, the intake email button, and the landing page profile section) is Team365-only and never shown to them.
        </p>

        {partner.partner_type === 'affiliate' && (
          <div className="mb-6">
            <div className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full mb-3 ${
              link_active ? 'bg-reset-sage/10 text-reset-sage' : awaiting_approval ? 'bg-reset-terracotta/10 text-reset-terracotta' : 'bg-reset-gold/25 text-reset-brown'
            }`}>
              {link_active
                ? '● Their link and $1 offer are live'
                : awaiting_approval
                  ? '● Submitted — waiting on Team365 to review & approve'
                  : '○ Not active yet — waiting on their intake form'}
            </div>
            {link_active && trial_code && (
              <p className="text-xs text-gray-500 mb-3">
                Their $1 offer code: <span className="font-mono text-reset-brown font-medium">{trial_code.code}</span> — {trial_code.label}
              </p>
            )}

            <div className="flex items-center gap-4 flex-wrap mb-3">
              <Link
                to={`/admin/affiliates/${partner.id}/preview`}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-medium rounded-xl text-reset-brown hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
              >
                Preview affiliate's dashboard →
              </Link>
              {landing_page_preview_url ? (
                <a
                  href={landing_page_preview_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-medium rounded-xl text-reset-brown hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
                >
                  Preview affiliate's landing page →
                </a>
              ) : (
                <span className="text-xs text-gray-400">Landing page not ready yet — waiting on their intake form.</span>
              )}
              {confirmation_page_preview_url ? (
                <a
                  href={confirmation_page_preview_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-medium rounded-xl text-reset-brown hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
                >
                  Preview affiliate's confirmation page →
                </a>
              ) : (
                <span className="text-xs text-gray-400">Confirmation page not ready yet — waiting on their intake form.</span>
              )}
            </div>

            {partner.profile_submitted_at && (
              <div className="mb-3">
                {link_active ? (
                  <button
                    onClick={handleUnapprove}
                    disabled={approving}
                    className="px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                  >
                    {approving ? 'Working…' : 'Take offline'}
                  </button>
                ) : (
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="px-4 py-2 bg-reset-sage text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
                  >
                    {approving ? 'Working…' : 'Approve — go live'}
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <button
                  onClick={handleOpenIntakePreview}
                  disabled={loadingPreview}
                  className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50"
                >
                  {loadingPreview ? 'Loading…' : partner.profile_submitted_at ? 'Review & resend landing page intake email' : 'Review & send landing page intake email'}
                </button>
                {intakeSent && <span className="text-xs text-reset-sage ml-2">Sent.</span>}
              </div>
              {link_active && (
                <div>
                  <button
                    onClick={handleOpenApprovalPreview}
                    className="text-xs font-medium text-reset-terracotta hover:underline"
                  >
                    Review & send "it's live" email
                  </button>
                  {approvalSent && <span className="text-xs text-reset-sage ml-2">Sent.</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {intakePreview && (
          <EmailReviewModal
            preview={intakePreview}
            setPreview={setIntakePreview}
            rendering={renderingHtml}
            sending={sendingIntake}
            onCancel={() => setIntakePreview(null)}
            onSend={handleSendIntakeEmail}
            extraFooterLink={{ href: intakePreview.intakeUrl, label: 'Preview their form →' }}
          />
        )}

        {approvalEmailPreview && (
          <EmailReviewModal
            preview={approvalEmailPreview}
            setPreview={setApprovalEmailPreview}
            rendering={renderingApprovalHtml}
            sending={sendingApproval}
            onCancel={() => setApprovalEmailPreview(null)}
            onSend={handleSendApprovalEmail}
            extraFooterLink={{ href: approvalEmailPreview.dashboardUrl, label: 'Preview their dashboard →' }}
          />
        )}

        {editing ? (
          <EditPartnerForm
            partner={partner}
            onSaved={() => { setEditing(false); load(); }}
            onCancel={() => setEditing(false)}
            onError={setError}
          />
        ) : (
          <p className="text-sm text-gray-500 mb-6">
            {partner.commission_rate_pct}% {partner.partner_type === 'royalty' ? 'on every paying member' : 'on people they refer'}
            {partner.partner_type === 'affiliate' && partner.referral_code && <> &middot; link code: <span className="font-mono">{partner.referral_code}</span></>}
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
          <StatCard label="Owed" value={`$${Number(owed).toFixed(2)}`} highlight />
          <StatCard label="Paid to date" value={`$${Number(paid).toFixed(2)}`} />
          {referral_count != null && <StatCard label="Referrals" value={referral_count} />}
        </div>

        {referred_members != null && (
          <>
            <h2 className="text-sm font-bold text-reset-brown mb-3">Referred members</h2>
            {referred_members.length === 0 ? (
              <p className="text-sm text-gray-400 mb-8">No one yet — referrals will show up here as soon as their link brings in a real member.</p>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50 mb-8">
                {referred_members.map(m => (
                  <Link key={m.id} to={`/admin/members/${m.id}`} className="px-5 py-3 flex flex-col gap-1 text-sm hover:bg-reset-cream/40">
                    <div className="flex items-center justify-between">
                      <span className="text-reset-brown font-medium">{m.name}</span>
                      <MemberStatusBadge status={m.status} label={m.status_label} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Member since {m.member_since ? new Date(m.member_since).toLocaleDateString() : '—'}</span>
                      <span>
                        {m.monthly_amount != null
                          ? `$${Number(m.monthly_amount).toFixed(2)}${m.currency ? '/' + m.interval : ''} · last paid ${m.last_payment_at ? new Date(m.last_payment_at).toLocaleDateString() : 'never'}`
                          : ''}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {partner.partner_type === 'affiliate' && (
          <>
            <h2 className="text-sm font-bold text-reset-brown mb-3">Landing page profile</h2>
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-8 space-y-4">
              <p className="text-xs text-gray-400">
                {partner.profile_submitted_at
                  ? `Submitted ${new Date(partner.profile_submitted_at).toLocaleString(undefined, { hour12: true })} — everything here feeds directly into their landing/confirmation page.`
                  : "Not submitted by the affiliate yet — Team365 can still set a photo directly below; it feeds the same landing/confirmation page."}
              </p>
              <div className="flex gap-4">
                <div className="shrink-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Photo</p>
                  <ImageDropZone
                    currentUrl={partner.photo_url}
                    onUploaded={(url) => onUpdatePhoto(url)}
                    onClear={() => onUpdatePhoto(null)}
                  />
                </div>
                <div className="space-y-2 text-sm min-w-0">
                  {partner.story_text && <p><span className="font-medium text-reset-brown">Story:</span> <span className="text-gray-600">{partner.story_text}</span></p>}
                  {partner.audience_text && <p><span className="font-medium text-reset-brown">Sending to:</span> <span className="text-gray-600">{partner.audience_text}</span></p>}
                  {partner.quote_text && <p><span className="font-medium text-reset-brown">Quote:</span> <span className="text-gray-600">{partner.quote_text}</span></p>}
                  {partner.do_not_mention_text && <p><span className="font-medium text-reset-brown">Don't mention:</span> <span className="text-gray-600">{partner.do_not_mention_text}</span></p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Landing page video</p>
                  {partner.landing_video_embed_url ? (
                    <div className="aspect-video rounded-xl overflow-hidden bg-reset-brownDeep">
                      <iframe src={partner.landing_video_embed_url} title="Landing page video" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowFullScreen className="w-full h-full border-0" />
                    </div>
                  ) : <p className="text-xs text-gray-400">Not submitted, or still encoding — check back shortly.</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Confirmation video</p>
                  {partner.confirmation_video_embed_url ? (
                    <div className="aspect-video rounded-xl overflow-hidden bg-reset-brownDeep">
                      <iframe src={partner.confirmation_video_embed_url} title="Confirmation video" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowFullScreen className="w-full h-full border-0" />
                    </div>
                  ) : <p className="text-xs text-gray-400">Not submitted, or still encoding — check back shortly.</p>}
                </div>
              </div>
            </div>
          </>
        )}

        <h2 className="text-sm font-bold text-reset-brown mb-3">Commissions</h2>
        {commissions.length === 0 ? (
          <p className="text-sm text-gray-400 mb-8">No commissions yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50 mb-8">
            {commissions.map(c => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{new Date(c.earned_at).toLocaleDateString()}</span>
                <span className="text-reset-brown font-medium">${Number(c.commission_amount).toFixed(2)}</span>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                  c.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        )}

        <h2 className="text-sm font-bold text-reset-brown mb-3">Payout history</h2>
        {payouts.length === 0 ? (
          <p className="text-sm text-gray-400">No payouts recorded yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {payouts.map(p => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{new Date(p.payout_date).toLocaleDateString()}</span>
                <span className="text-reset-brown font-medium">${Number(p.amount).toFixed(2)}</span>
                {p.note && <span className="text-xs text-gray-400">{p.note}</span>}
              </div>
            ))}
          </div>
        )}

        <MediaSection partnerId={partner.id} />

        {showPayout && (
          <PayoutModal
            partner={partner}
            owed={owed}
            onClose={() => setShowPayout(false)}
            onRecorded={() => { setShowPayout(false); load(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

/**
 * Real FEA Create feature ("Affiliate Manager Media") confirmed via Jenn's
 * screenshots 2026-08-29 — downloadable files an affiliate can grab from
 * their own dashboard. A file uploaded here for THIS partner shows only to
 * them; leave "for everyone" checked to make it universal instead.
 */
function MediaSection({ partnerId }) {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [everyone, setEveryone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = useCallback(() => {
    affiliateService.listMedia(partnerId).then(setMedia).catch(() => setError('Could not load media.'));
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  // Accepts any number of files — dragging a batch in (or multi-selecting
  // via the browse dialog) uploads and attaches every one of them, not
  // just the first. Jenn, 2026-09-10: dragged "a bunch of pictures" in at
  // once and nothing happened — this had neither drag-and-drop nor
  // multi-file support at all.
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const url = await uploadImage(file);
        await affiliateService.createMedia({
          partnerId: everyone ? null : partnerId,
          title: files.length === 1 ? (title.trim() || file.name) : file.name,
          fileUrl: url,
        });
      }
      setTitle('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload one of these files.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this file from the affiliate dashboard(s)?')) return;
    await affiliateService.deleteMedia(id);
    load();
  };

  // Corrects a file's sharing scope after the fact — no more deleting and
  // re-uploading just to fix whether the "for everyone" toggle was right
  // at the moment it was dropped in.
  const handleToggleSharing = async (m) => {
    try {
      await affiliateService.updateMediaSharing(m.id, m.partner_id === null ? partnerId : null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update this file's sharing.");
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-gray-100">
      <h2 className="text-sm font-bold text-reset-brown mb-3">Affiliate media</h2>
      <p className="text-xs text-gray-400 mb-4">Files this affiliate (or every affiliate, if marked) can download from their own dashboard.</p>

      {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        className={`bg-white rounded-2xl shadow-sm p-4 mb-4 flex items-center gap-3 flex-wrap border-2 border-dashed transition-colors
          ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-transparent'}`}
      >
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="File title (optional — only used for a single file)"
          className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <label className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
          <input type="checkbox" checked={everyone} onChange={e => setEveryone(e.target.checked)} />
          For every affiliate
        </label>
        <span className="text-xs text-gray-400 shrink-0">Drag files here, or</span>
        <input ref={fileInputRef} type="file" multiple onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} className="hidden" />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50 shrink-0"
        >
          {uploading ? 'Uploading…' : 'Upload file'}
        </button>
      </div>

      {!media ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : media.length === 0 ? (
        <p className="text-sm text-gray-400">No files yet.</p>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {media.map(m => (
              <div key={m.id} className="border border-gray-100 rounded-xl overflow-hidden">
                <a href={m.file_url} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-reset-cream/60">
                  <img src={m.file_url} alt={m.title} className="w-full h-full object-cover" />
                </a>
                <p className="text-xs text-gray-500 px-2 pt-1.5 truncate">{m.title}</p>
                <div className="flex items-center justify-between gap-1 px-2 pb-2 pt-1">
                  <button
                    onClick={() => handleToggleSharing(m)}
                    title="Click to change who this is shared with"
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full hover:opacity-75 truncate ${
                      m.partner_id === null ? 'bg-reset-cream text-reset-brown' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {m.partner_id === null ? 'Everyone' : 'This affiliate only'}
                  </button>
                  <button onClick={() => handleDelete(m.id)} className="text-xs text-red-400 hover:text-red-600 shrink-0">Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Shared review-before-send modal for both the intake email and the
 * approval ("it's live") email — same real editable subject/rich-text
 * body, header-banner choice, and live styled preview for either. Jenn,
 * 2026-09-11: "we need to be able to choose our banners, and have a live
 * preview...the banner image needs to be seen. And we need to be able to
 * format the text" — reuses RichTextEditor and the same header choices as
 * the bulk email composer (AdminBulkEmail.jsx) rather than a second,
 * divergent editor.
 */
function EmailReviewModal({ preview, setPreview, rendering, sending, onCancel, onSend, extraFooterLink }) {
  const headerStyle = preview.headerStyle || 'jenn_photo';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-6 pb-4 border-b border-gray-100 shrink-0">
          <h2 className="text-sm font-bold text-reset-brown mb-1">Review before sending</h2>
          <p className="text-xs text-gray-400">To {preview.toEmail} — edit any of it; the preview on the right updates to match, exactly what they'll actually see.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
          <div className="p-6 overflow-y-auto border-r border-gray-100">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Banner</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {HEADER_STYLES.map(hs => (
                <button
                  key={hs.key}
                  onClick={() => setPreview({ ...preview, headerStyle: hs.key })}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    headerStyle === hs.key ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-reset-brown block">{hs.label}</span>
                  <span className="text-gray-400">{hs.description}</span>
                </button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Subject</label>
            <input
              value={preview.subject}
              onChange={e => setPreview({ ...preview, subject: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />

            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
            <RichTextEditor
              value={preview.body}
              onChange={(body) => setPreview({ ...preview, body })}
              minHeightClass="min-h-[280px]"
              placeholder="Select text to bold or italicize it."
            />
          </div>

          <div className="bg-gray-50 flex flex-col min-h-0">
            <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100 shrink-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What they'll actually see</p>
              {rendering && <span className="text-xs text-gray-400">Updating…</span>}
            </div>
            <iframe
              title="Email preview"
              srcDoc={preview.html || '<p style="font-family:sans-serif;color:#999;padding:20px">Loading preview…</p>'}
              className="flex-1 w-full border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        <div className="p-6 pt-4 border-t border-gray-100 shrink-0 flex items-center justify-between gap-3">
          {extraFooterLink ? (
            <a href={extraFooterLink.href} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-reset-terracotta hover:underline">
              {extraFooterLink.label}
            </a>
          ) : <span />}
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
            <button
              onClick={onSend}
              disabled={sending}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send this email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl shadow-sm px-4 py-3 text-center ${highlight ? 'bg-reset-terracotta text-white' : 'bg-white'}`}>
      <p className={`text-xl font-bold ${highlight ? 'text-white' : 'text-reset-brown'}`}>{value}</p>
      <p className={`text-xs mt-0.5 ${highlight ? 'text-white/80' : 'text-gray-500'}`}>{label}</p>
    </div>
  );
}

function EditPartnerForm({ partner, onSaved, onCancel, onError }) {
  const [name, setName] = useState(partner.name || '');
  const [email, setEmail] = useState(partner.email || '');
  const [commissionRatePct, setCommissionRatePct] = useState(partner.commission_rate_pct);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await affiliateService.updatePartner(partner.id, {
        name: name.trim(), email: email.trim(), commission_rate_pct: Number(commissionRatePct),
      });
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not save these changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
          <input value={name} onChange={e => setName(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Commission %</label>
          <input type="number" min="0" max="100" step="0.1" value={commissionRatePct} onChange={e => setCommissionRatePct(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
      </div>
      <p className="text-[11px] text-gray-400">Partner type and referral code can't be changed here — archive this partner and create a new one under a different type if needed.</p>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
      </div>
    </form>
  );
}

function PayoutModal({ partner, owed, onClose, onRecorded }) {
  const [amount, setAmount] = useState(Number(owed).toFixed(2));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await affiliateService.recordPayout(partner.id, { amount: Number(amount), note: note.trim() || null });
      onRecorded();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record this payout.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-5 w-full max-w-sm space-y-3">
        <h3 className="font-semibold text-reset-brown">Record payout &mdash; {partner.name}</h3>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Note (optional)</label>
          <input value={note} onChange={e => setNote(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Record payout'}
          </button>
        </div>
      </form>
    </div>
  );
}
